import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { setContext } from '@apollo/client/link/context';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import {
  getSessionToken,
  isSessionTokenExpired,
  refresh,
} from '@descope/react-sdk';
import { config, graphqlApiUrl } from '@config/index';
import {
  errorEmitterLink,
  onGraphQLError,
} from '@utils/apollo-error-emitter-link';
import { logGraphQLErrorResponse } from '@utils/log-graphql-error-response';

const descopeAuthLink = setContext((_, { headers, ...context }) => {
  try {
    if (isSessionTokenExpired?.()) {
      void refresh?.();
    }

    const sessionToken = getSessionToken?.();

    return {
      ...context,
      headers: {
        ...headers,
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      },
    };
  } catch {
    // Catch any Descope SDK errors silently and return context as-is
    return {
      ...context,
      headers: {
        ...headers,
      },
    };
  }
});

const portalHeaderLink = setContext((_, { headers, ...context }) => {
  return {
    ...context,
    headers: {
      ...headers,
      Portal: 'pharmacy',
    },
  };
});

export const client = new ApolloClient({
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
    watchQuery: {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  },
  devtools: {
    enabled: !config.isProduction,
  },
  link: ApolloLink.from([
    // Apollo adds `__typename` to every query result, so it rides along when a
    // result is reused as mutation input. portal-api ignores it, but Hive Router
    // rejects any input field not in the schema, `__typename` included.
    removeTypenameFromVariables(),
    errorEmitterLink,
    portalHeaderLink,
    descopeAuthLink,
    new HttpLink({
      credentials: 'include',
      uri: graphqlApiUrl,
    }),
  ]),
});

onGraphQLError(logGraphQLErrorResponse);

if (!config.isProduction) {
  loadDevMessages();
  loadErrorMessages();
}
